'use client'

import {Select as ChakraSelect, Field, Portal, SelectRootProps, Text, createListCollection} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {ReactNode, useMemo} from 'react'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'
import {useSelectField} from './use-select-field.js'

export interface SelectItem {
  label: string
  value: string
  description?: string | null
}

interface Props extends Omit<SelectRootProps, 'collection'> {
  items: SelectItem[]
  label: ReactNode
  hideLabel?: boolean
  helpText?: string
  description?: string
  placeholder?: string
  controlProps?: ChakraSelect.ControlProps
  contentProps?: ChakraSelect.ContentProps
  portalled?: boolean
  emptyMessage?: string
  displayClearTrigger?: boolean
  hideErrorMessage?: boolean
  errorMessagePadding?: number
}

export function Select({
  items,
  label,
  hideLabel,
  placeholder,
  required,
  children,
  controlProps,
  contentProps,
  description,
  helpText,
  displayClearTrigger,
  errorMessagePadding,
  portalled = true,
  hideErrorMessage,
  emptyMessage,
  ...props
}: Props) {
  const {t} = useTranslate('common')
  const {field, value, handleChange} = useSelectField()

  const collection = useMemo(() => {
    return createListCollection({items})
  }, [items])

  const isInvalid = field.state.meta.errors.length > 0

  return (
    <FieldRoot required={required} disabled={props.disabled}>
      <ChakraSelect.Root
        collection={collection}
        name={field.name}
        value={value}
        onValueChange={e => handleChange(e.value)}
        onBlur={field.handleBlur}
        {...props}
      >
        <ChakraSelect.HiddenSelect />

        <Field.Label asChild>
          <ChakraSelect.Label srOnly={hideLabel}>
            {label} {required && <FieldRequiredIndicator />}
          </ChakraSelect.Label>
        </Field.Label>

        {helpText && <Field.HelperText srOnly={hideLabel}>{helpText}</Field.HelperText>}

        {!children && (
          <ChakraSelect.Control backgroundColor="bg" {...controlProps}>
            <ChakraSelect.Trigger>
              <ChakraSelect.ValueText
                placeholder={placeholder}
                color={isInvalid && hideErrorMessage ? 'red' : undefined}
              />
            </ChakraSelect.Trigger>
            <ChakraSelect.IndicatorGroup>
              {displayClearTrigger && <ChakraSelect.ClearTrigger />}
              <ChakraSelect.Indicator />
            </ChakraSelect.IndicatorGroup>
          </ChakraSelect.Control>
        )}

        {description && (
          <Field.HelperText mt={2} srOnly={description}>
            {description}
          </Field.HelperText>
        )}

        {children && (
          <ChakraSelect.Control {...controlProps}>
            <ChakraSelect.Trigger asChild unstyled>
              {children}
            </ChakraSelect.Trigger>
          </ChakraSelect.Control>
        )}

        <Portal disabled={!portalled}>
          <ChakraSelect.Positioner>
            <ChakraSelect.Content minWidth="16rem" {...contentProps}>
              {!collection.items.length && (
                <Text padding="1" fontSize="xs" color="fg.muted">
                  {emptyMessage ? emptyMessage : t('no_options_available')}
                </Text>
              )}

              {collection.items.map(item => (
                <ChakraSelect.Item item={item} key={item.value}>
                  {item.label}
                  <ChakraSelect.ItemIndicator />
                </ChakraSelect.Item>
              ))}
            </ChakraSelect.Content>
          </ChakraSelect.Positioner>
        </Portal>
      </ChakraSelect.Root>

      <ErrorMessage errorMessagePadding={errorMessagePadding} srOnly={hideErrorMessage} />
    </FieldRoot>
  )
}
